import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { Account } from 'msal';
import Button from '@material-ui/core/Button';
import AddIcon from '@material-ui/icons/Add';
import grey from '@material-ui/core/colors/grey';
import { Grid, makeStyles, Box, Typography, Theme } from '@material-ui/core';
import Modal from '../../components/Modal';
import Loader from '../../components/Loader';
import DatePicker from '../../components/DatePicker';
import Title from '../../components/PageHeader/Title';
import Header from '../../components/PageHeader/Header';
import TimeSheetInfiniteList from './TimeSheetInfiniteList';
import { TimeSheetCountType, TimeSheetList, TimeSheet } from '../../store/modules/timeSheet/type';
import EditTimeSheet from './EditTimeSheet';
import Filters from '../../components/Filters';
import { extractFormData } from 'src/components/FormDisplay/FormDisplay';
import { FilterCondition, Form, MergeTemplate, Order } from '../../store/modules/application/types';

type TimeSheetProps = {
    hasMore: boolean;
    isLoading: boolean;
    timeSheet: TimeSheet;
    isFilteredLoading: boolean;
    timeSheetList: TimeSheetList;
    timeSheetCount: TimeSheetCountType;
    getTimeSheetList: Function;
    getTimeSheetTypes: Function;
    getTimeSheetDetails: Function;
    getTimeSheetCategories: Function;
    getTimeExpenseCategories: Function;
    openTimeSheetForSelectedDate: Function;
    getMergeTemplates: Function;
    mergeTimesheetDocuments: Function;
    mergeTemplateList: MergeTemplate[];
    isSearchFormLoading: boolean;
    searchForm: Form;
    getSearchForm: Function;
    applyTimesheetFilters: Function;
    loggedInUser: Account;
    filterSelections: Record<string, string | string[]>;
    setFilterSelections: Function;
};

const useStyles = makeStyles((theme: Theme) => ({
    addTimeSheet: {
        color: 'white',
        marginRight: '40px',
        fontWeight: 600,
        cursor: 'pointer',
        letterSpacing: '0.3px,',
        display: 'flex',
        alignItems: 'center',
        marginBottom: '10px',
        borderRadius: '4px',
        backgroundColor: '#0277BD !important',
        marginTop: 10,
        padding: '5px 15px',
    },
    timeSheetContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    activationModal: {
        padding: 10,
    },
    modalHeader: {
        width: '100%',
        color: theme.palette.text.secondary,

        fontSize: 20,
        fontWeight: 500,
        marginBottom: 10,
    },
    addTimeSheetDialogMain: {
        width: 'auto',
    },
    addTimeSheetDialogLabel: {
        fontSize: 14,
        fontWeight: 500,
        color: theme.palette.text.secondary,

        textAlign: 'end',
        justifyContent: 'flex-start',
        width: '100%',
        lineHeight: '21px',
        display: 'flex',
    },
    addTimeSheetDialogValue: {
        fontSize: 14,
        fontWeight: 450,
        color: grey[600],
        width: '100%',
        lineHeight: '21px',
    },
    datePickerContainer: {
        backgroundColor: 'white',
        border: '1px solid #ced4da',
        borderRadius: 5,
        padding: '3px 0px 3px 10px',
        margin: '0px 0px',
    },
    datePicker: {
        margin: 0,
    },
    underline: {
        '&:before': {
            borderBottomColor: 'white',
        },
        '&:after': {
            borderBottomColor: 'white',
        },
        '&:hover:before': {
            borderBottomColor: ['white', '!important'],
        },
    },
    btnContainer: {
        marginTop: 25,
    },
    modalBtn: {
        marginLeft: 0,
        marginRight: '10px',
    },
    content: {
        fontSize: '16px',
        marginBottom: '20px',
        marginTop: '10px',
        width: '100%',
    },
    tablePaginationInfo: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '10px',
    },
    countSpan: {
        color: '#484848',
        fontSize: 14,
        letterSpacing: 0.1,
        display: 'flex',
        alignItems: 'center',
        marginLeft: '40px',
    },
    metadataContainer: {
        display: 'flex',
        justifyContent: 'space-between',
    },
}));

const TimeSheetEntries: FunctionComponent<TimeSheetProps> = (props) => {
    const limit = 20;
    const [skip, setSkip] = useState<number>(0);
    const [timeSheetSortOrder, setTimeSheetSortOrder] = useState<Order>('desc');
    const [timeSheetSortColumn, setTimeSheetSortColumn] = useState<string>('startDate');

    const {
        hasMore,
        isLoading,
        timeSheetList,
        timeSheet,
        timeSheetCount,
        getTimeSheetList,
        isFilteredLoading,
        getTimeSheetTypes,
        getTimeSheetDetails,
        getTimeSheetCategories,
        getTimeExpenseCategories,
        openTimeSheetForSelectedDate,
        getMergeTemplates,
        mergeTimesheetDocuments,
        mergeTemplateList,
        isSearchFormLoading,
        searchForm,
        getSearchForm,
        applyTimesheetFilters,
        loggedInUser,
        filterSelections,
        setFilterSelections,
    } = props;
    const classes = useStyles();
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isAddTimeSheetOpen, setIsTimeSheetModalOpen] = useState<boolean>(false);
    const [timeSheetDate, setTimeSheetDate] = useState(moment().format('YYYY-MM-DD'));
    const [listFilters, setListFilters] = useState<FilterCondition[]>([]);
    const defaultValue = { user: { id: [loggedInUser?.accountIdentifier] } };

    useEffect(() => {
        getSearchForm();
    }, [getSearchForm]);

    const refreshEventsList = useCallback(
        (listFilters, apiProps) => {
            // check if filterSelections from application state have been applied before refreshing results
            if (
                !!filterSelections &&
                (Object.entries(filterSelections).length === 0 ||
                    (Object.entries(filterSelections).length > 0 && listFilters.length > 0))
            )
                applyTimesheetFilters(listFilters, {
                    limit,
                    orderBy: timeSheetSortOrder,
                    orderCol: timeSheetSortColumn,
                    ...apiProps,
                });
        },
        [applyTimesheetFilters, limit, timeSheetSortOrder, timeSheetSortColumn, filterSelections],
    );

    useEffect(() => {
        refreshEventsList(listFilters, { skip });
        // The disable-next-line below is to avoid redundant callbacks due to updates in listFilters
        //eslint-disable-next-line
    }, [refreshEventsList, skip]);

    useEffect(() => {
        refreshEventsList(listFilters, { skip: 0 });
        setSkip(0);
    }, [refreshEventsList, listFilters]);

    const handleSelectDateModalStatus = (isOpen: boolean) => {
        setIsModalOpen(isOpen);
    };

    const handleAddTimeSheetModalStatus = (isOpen: boolean) => {
        setIsModalOpen(false);
        setIsTimeSheetModalOpen(isOpen);
        isOpen && openTimeSheetForSelectedDate(moment(timeSheetDate).format('YYYY-MM-DD'));
    };

    const onSelectTimeSheetRow = (id: string) => {
        setIsTimeSheetModalOpen(true);
        getTimeSheetDetails(id);
    };

    useEffect(() => {
        getTimeSheetTypes();
        getTimeSheetCategories();
        getTimeExpenseCategories();
    }, [getTimeSheetTypes, getTimeSheetCategories, getTimeExpenseCategories]);
    useEffect(() => {
        const payload = {
            where: {
                type: 'Timesheet',
            },
            fields: {
                buffer: false,
            },
        };
        getMergeTemplates(payload);
    }, [getMergeTemplates]);

    const updateTimesheetSortBy = (sortBy: string) => {
        setTimeSheetSortColumn(sortBy);
        setSkip(0);
    };
    const updateTimesheetSortOrder = (sortOrder: Order) => {
        setTimeSheetSortOrder(sortOrder);
        setSkip(0);
    };

    return (
        <div className="verticalFillContainer">
            <Header>
                <Grid container md={2} lg={2} direction="row" alignItems="center">
                    <Title titleText="Time & Expense" />
                </Grid>
                <Grid item md={10} lg={10}>
                    <Filters
                        searchForm={searchForm}
                        isLoading={isSearchFormLoading}
                        setListFilters={setListFilters}
                        selections={!!searchForm && (filterSelections || extractFormData(defaultValue, searchForm))}
                        handleFilterSelections={setFilterSelections}
                    />
                </Grid>
            </Header>
            <Box className={classes.metadataContainer}>
                <Box className={classes.tablePaginationInfo}>
                    <span className={classes.countSpan}>{`${timeSheetCount.totalTimeSheetCount || 0} Results`}</span>
                </Box>
                <div className={classes.timeSheetContainer}>
                    <Button
                        startIcon={<AddIcon />}
                        className={classes.addTimeSheet}
                        onClick={() => {
                            setTimeSheetDate(moment().format('YYYY-MM-DD'));
                            handleSelectDateModalStatus(true);
                        }}
                    >
                        OPEN TIMESHEET
                    </Button>
                </div>
            </Box>

            <div className="verticalFillContent">
                {(!timeSheetList?.length && isLoading) || !filterSelections ? (
                    <Loader />
                ) : (
                    <TimeSheetInfiniteList
                        getTimeSheetList={getTimeSheetList}
                        timeSheetList={timeSheetList}
                        isLoading={isLoading}
                        skip={skip}
                        limit={limit}
                        setSkip={setSkip}
                        hasMore={hasMore}
                        timeSheetCount={timeSheetCount}
                        setSortColumn={updateTimesheetSortBy}
                        setSortOrder={updateTimesheetSortOrder}
                        sortOrder={timeSheetSortOrder}
                        sortBy={timeSheetSortColumn}
                        isFilteredLoading={isFilteredLoading}
                        onSelectTimeSheetRow={onSelectTimeSheetRow}
                    />
                )}
            </div>

            {isModalOpen && (
                <Modal open={true} maxWidth={'sm'} fullWidth={false} minHeight={'10vh'}>
                    <Grid container className={classes.activationModal}>
                        <div className={classes.modalHeader}>Select a date</div>
                        <Typography variant="h6" className={classes.content}>
                            Open timesheet for the selected date{' '}
                        </Typography>
                        <Box component="div" className={classes.addTimeSheetDialogMain}>
                            <Grid container style={{ marginTop: '10px' }}>
                                <Grid
                                    item
                                    xs={3}
                                    sm={3}
                                    md={3}
                                    lg={3}
                                    style={{ display: 'flex', alignItems: 'center' }}
                                >
                                    <Box component="div" className={classes.addTimeSheetDialogLabel}>
                                        Date
                                    </Box>
                                </Grid>
                                <Grid item xs={9} sm={9} md={9} lg={9}>
                                    <Box className={classes.addTimeSheetDialogValue}>
                                        <DatePicker
                                            containerClasses={classes.datePickerContainer}
                                            datePickerClasses={classes.datePicker}
                                            inputProps={{
                                                classes: {
                                                    underline: classes.underline,
                                                },
                                            }}
                                            value={timeSheetDate}
                                            handleDate={(date: string) => {
                                                setTimeSheetDate(date);
                                            }}
                                            fullWidth={true}
                                            label=""
                                            marginNone={true}
                                        ></DatePicker>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                        <Grid className={classes.btnContainer} sm={12} md={12} lg={12} item>
                            <Box display="flex" justifyContent="flex-end">
                                <Button
                                    onClick={() => {
                                        handleSelectDateModalStatus(false);
                                    }}
                                    className={classes.modalBtn}
                                >
                                    CANCEL
                                </Button>
                                <Button
                                    className={classes.modalBtn}
                                    onClick={() => handleAddTimeSheetModalStatus(true)}
                                    variant="contained"
                                    color="primary"
                                    disabled={!moment(timeSheetDate).isValid()}
                                >
                                    ADD
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Modal>
            )}
            {isAddTimeSheetOpen && (
                <Modal open={true} padding={0} maxWidth={'lg'} fullWidth={true} minHeight={'10vh'} height={'90vh'}>
                    <EditTimeSheet
                        setSkip={setSkip}
                        timeSheet={timeSheet}
                        listFilters={listFilters}
                        timeSheetDate={timeSheetDate}
                        handleAddTimeSheetModalStatus={handleAddTimeSheetModalStatus}
                        mergeTimesheetDocuments={mergeTimesheetDocuments}
                        mergeTemplatesList={mergeTemplateList}
                    />
                </Modal>
            )}
        </div>
    );
};
export default TimeSheetEntries;
